
import { useState } from 'react';
import { Play } from 'lucide-react';
import AnimatedCounter from '@/components/AnimatedCounter';

interface HeroSectionV2Props {
  onPhoneClick: () => void;
}

const HeroSectionV2 = ({ onPhoneClick }: HeroSectionV2Props) => {
  const [hasClicked, setHasClicked] = useState(false);

  const handlePhoneClick = () => {
    setHasClicked(true);
    onPhoneClick();
    setTimeout(() => setHasClicked(false), 2500);
  };

  return (
    <section className="container max-w-7xl mx-auto px-4 py-12 relative">
      {/* Enhanced Background Bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-yellow-400/30 rounded-full blur-xl animate-pulse-gentle"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-green-400/40 rounded-full blur-lg animate-pulse-gentle delay-1000"></div>
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-white/20 rounded-full blur-2xl animate-pulse-gentle delay-500"></div>
        <div className="absolute top-1/3 left-1/2 w-20 h-20 bg-orange-accent/30 rounded-full blur-lg animate-pulse-gentle delay-700"></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-purple-300/25 rounded-full blur-xl animate-pulse-gentle delay-300"></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left side - Text content with new typography */}
        <div className="text-left lg:pr-8">
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-[0.85] tracking-tighter">
            Really,
            <span className="block text-yellow-400 mt-1">show up</span>
            <span className="block mt-1">every day</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed font-bold">
            Simple commitments. Visual progress. Daily wins.
            <br />
            Build the habits that matter with Show Up!
          </p>

          {/* Social Proof Counter */}
          <div className="text-white">
            <div className="text-5xl md:text-6xl font-black mb-4">
              <AnimatedCounter target={847} className="text-yellow-400" />
            </div>
            <p className="text-xl text-white/90 font-bold">people already on waitlist</p>
            <div className="w-24 h-2 bg-yellow-400/60 rounded-full mt-4"></div>
          </div>
        </div>

        {/* Right side - Tilted Interactive Phone Mockup */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative group">
            {/* Enhanced iPhone Frame with Tilt */}
            <div className="w-[320px] h-[640px] bg-black rounded-[3.5rem] p-4 shadow-2xl transform rotate-12 hover:rotate-6 transition-all duration-700 hover:scale-105">
              <div className="w-full h-full bg-white rounded-[3rem] overflow-hidden relative">
                {/* Realistic Status Bar */}
                <div className="h-12 bg-gray-50 flex items-center justify-between px-8 text-sm font-semibold border-b border-gray-100">
                  <span className="text-black">9:41</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                    </div>
                    <div className="w-6 h-3 bg-green-500 rounded-sm"></div>
                  </div>
                </div>
                
                {/* Enhanced App Content */}
                <div className="p-8 h-full bg-gradient-to-b from-purple-50 via-white to-purple-50/30">
                  <div className="mb-8">
                    <h2 className="text-3xl font-black text-slate-800 mb-3">Today's PACT</h2>
                    <p className="text-slate-600 font-medium">Ready to show up?</p>
                  </div>
                  
                  <div className="bg-white rounded-3xl p-8 shadow-lg border border-purple-100 mb-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                        <span className="text-2xl">💪</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-slate-800">Morning Exercise</h3>
                        <p className="text-slate-600 font-medium">30 minutes of movement</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-sm font-semibold text-slate-500">Day 12 of 30</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className="w-2/5 h-full bg-gradient-to-r from-purple-500 to-yellow-400 rounded-full"></div>
                        </div>
                        <span className="text-sm font-bold text-purple-600">40%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Interactive Button */}
                  <button
                    onClick={handlePhoneClick}
                    disabled={hasClicked}
                    className={`w-full py-5 rounded-3xl font-black text-xl transition-all duration-500 transform ${
                      hasClicked 
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white scale-95' 
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {hasClicked ? '🎉 Completed!' : 'Click Here to Complete'}
                  </button>
                  
                  {!hasClicked && (
                    <div className="flex items-center justify-center mt-6 text-purple-600 animate-bounce">
                      <Play size={18} className="mr-2" />
                      <span className="text-sm font-semibold">Tap to see magic happen</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Enhanced floating elements positioned around the tilted phone */}
            <div className="absolute -top-16 -left-16 w-28 h-28 bg-yellow-400/40 rounded-full blur-xl animate-pulse-gentle"></div>
            <div className="absolute -bottom-20 -right-20 w-36 h-36 bg-green-400/30 rounded-full blur-2xl animate-pulse-gentle delay-1000"></div>
            <div className="absolute top-1/4 -right-12 w-20 h-20 bg-white/40 rounded-full blur-lg animate-pulse-gentle delay-500"></div>
            <div className="absolute bottom-1/3 -left-8 w-24 h-24 bg-orange-accent/30 rounded-full blur-xl animate-pulse-gentle delay-800"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionV2;
